import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { getAllTeachers } from "../api/getAllTeachers";
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const TeacherListLayer = () => {
  const [teachers, setTeachers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const teachersPerPage = 10;

  useEffect(() => {
    const fetchTeachers = async () => {
      const data = await getAllTeachers();
      setTeachers(data);
    };
    fetchTeachers();
  }, []);

  const filteredTeachers = teachers.filter(teacher => {
    const fullName = `${teacher.firstname || ''} ${teacher.lastname || ''}`.toLowerCase();
    const fullText = `${fullName} ${teacher.email}`.toLowerCase();
    const matchesSearch = fullText.includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === '' || teacher.active === (statusFilter === 'Active' ? '1' : '0');

    const joinDate = new Date(teacher.createddate);
    const afterStart = startDate ? joinDate >= new Date(startDate) : true;
    const beforeEnd = endDate ? joinDate <= new Date(endDate) : true;

    return matchesSearch && matchesStatus && afterStart && beforeEnd;
  });

  const indexOfLastTeacher = currentPage * teachersPerPage;
  const indexOfFirstTeacher = indexOfLastTeacher - teachersPerPage;
  const currentTeachers = filteredTeachers.slice(indexOfFirstTeacher, indexOfLastTeacher);
  const totalPages = Math.ceil(filteredTeachers.length / teachersPerPage);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  const exportToExcel = () => {
    const heading = [["Teacher List"]];
    const data = filteredTeachers.map((teacher, i) => ({
      "S.L": i + 1,
      "Join Date": new Date(teacher.createddate).toLocaleDateString(),
      "Name": `${teacher.firstname || ''} ${teacher.lastname || ''}`.trim(),
      "Email": teacher.email,
      "Phone Number": teacher.phonenumber || '-',
      "Country": teacher.country || '-',
      "Status": teacher.active === "1" ? "Active" : "Inactive"
    }));
    const worksheet = XLSX.utils.json_to_sheet(data, { origin: -1 });
    XLSX.utils.sheet_add_aoa(worksheet, heading, { origin: "A1" });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Teachers");
    XLSX.writeFile(workbook, "teachers.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Teacher List", 14, 20);
    const tableColumn = ["S.L", "Join Date", "Name", "Email", "Phone Number", "Country", "Status"];
    const tableRows = filteredTeachers.map((teacher, i) => ([
      i + 1,
      new Date(teacher.createddate).toLocaleDateString(),
      `${teacher.firstname || ''} ${teacher.lastname || ''}`.trim(),
      teacher.email,
      teacher.phonenumber || '-',
      teacher.country || '-',
      teacher.active === "1" ? "Active" : "Inactive"
    ]));
    autoTable(doc, {
      startY: 25,
      head: [tableColumn],
      body: tableRows,
    });
    doc.save("teachers.pdf");
  };

  // Spinner Loader when data is loading
  if (teachers.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '6px solid #e0e0e0',
          borderTop: '6px solid #45B369',
          borderRadius: '50%',
          animation: 'spin 1s ease-in-out infinite'
        }} />
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex align-items-center flex-wrap gap-3">
          <input
            type="text"
            className="form-control w-auto"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <input
            type="date"
            className="form-control w-auto"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <input
            type="date"
            className="form-control w-auto"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
          <select
            className="form-select form-select-sm w-auto"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
          <button
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('');
              setStartDate('');
              setEndDate('');
            }}
            className="btn btn-outline-secondary btn-sm"
          >
            Reset Filters
          </button>
          <button onClick={exportToExcel} className="btn btn-success btn-sm">Excel Export</button>
          <button onClick={exportToPDF} className="btn btn-danger btn-sm">PDF Export</button>
        </div>
      </div>

      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                <th>S.L</th>
                <th>Join Date</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone Number</th>
                <th>Country</th>
                <th>Subject</th>
                <th className="text-center">Status</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {currentTeachers.map((teacher, index) => (
                <tr key={teacher.id}>
                  <td>{indexOfFirstTeacher + index + 1}</td>
                  <td>{new Date(teacher.createddate).toLocaleDateString()}</td>
                  <td>
                    <div className="d-flex align-items-center">
                      <img
                        src={teacher.imagepath?.startsWith("http") ? teacher.imagepath : "/assets/images/default-user.png"}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/assets/images/default-user.png";
                        }}
                        alt="User"
                        className="w-40-px h-40-px rounded-circle me-12"
                      />
                      <span>{`${teacher.firstname || ''} ${teacher.lastname || ''}`.trim()}</span>
                    </div>
                  </td>
                  <td>{teacher.email}</td>
                  <td>{teacher.phonenumber || '-'}</td>
                  <td>{teacher.country || '-'}</td>
                  <td>{teacher.subject || '-'}</td>
                  <td className="text-center">
                    <span className={`badge ${teacher.active === "1" ? 'bg-success' : 'bg-danger'}`}>
                      {teacher.active === "1" ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="text-center">
                    <button className="btn btn-info btn-sm me-1"><Icon icon="majesticons:eye-line" /></button>
                    <button className="btn btn-success btn-sm me-1"><Icon icon="lucide:edit" /></button>
                    <button className="btn btn-danger btn-sm"><Icon icon="fluent:delete-24-regular" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="d-flex justify-content-between mt-3">
          <span>
            Showing {indexOfFirstTeacher + 1} to {Math.min(indexOfLastTeacher, filteredTeachers.length)} of {filteredTeachers.length} entries
          </span>
          <ul className="pagination">
            {[...Array(totalPages)].map((_, i) => (
              <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                <button onClick={() => handlePageChange(i + 1)} className="page-link">
                  {i + 1}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TeacherListLayer;
